import React, { useCallback, useEffect, useState } from 'react';
import { compact, flatten } from 'lodash';
import { components } from 'react-select';
import classNames from 'classnames';
import cogoToast from 'cogo-toast';
import PropTypes from 'prop-types';

import { DEBOUNCE_DURATION } from 'data/asyncSelect';
import { DEFAULT_ERROR_MESSAGE } from 'data/defaultErrorMessage';
import {
  useBulkSearchSuperBatchesMutation,
  useLazySearchSuperBatchesQuery,
} from 'services/superBatchService';
import FieldErrors from 'components/FieldErrors';
import GlobalLoader from 'components/GlobalLoader';
import Select from 'components/Select';
import styles from './SuperBatchSelect.module.scss';
import SuperBatchTypeChip from 'components/SuperBatchTypeChip';
import useDebouncedPromise from 'hooks/useDebouncedPromise';

const MIN_KEYWORD_LENGTH = 3;

function SuperBatchOption(props) {
  return (
    <components.Option {...props}>
      <div className="h4 row">
        {props.data.label}
        {props.data.batch_type && (
          <SuperBatchTypeChip batchType={props.data.batch_type} />
        )}
      </div>
    </components.Option>
  );
}

function SuperBatchInput(props) {
  return (
    <components.Input
      {...props}
      onPaste={props.selectProps.handleOnPaste}
    />
  );
}

// To enable bulk searching, the isMulti property will be required.

export default function SuperBatchSelect({
  excludedSuperBatchIds = [],
  batchTypes = [],
  superBatchGroupId,
  isDisabled,
  isMulti,
  value,
  onChange,
  containerClassName,
  ...remainingProps
}) {
  const [superBatches, setSuperBatches] = useState(value);
  const [fetchSuperBatches, { isFetching }] = useLazySearchSuperBatchesQuery();
  const [
    bulkFetchSuperBatches,
    { data, error, isLoading: isBulkFetching, isSuccess, reset },
  ] = useBulkSearchSuperBatchesMutation();

  const handleOnPaste = useCallback(
    async (event) => {
      bulkFetchSuperBatches({
        batchNames: event.clipboardData.getData('text'),
        batchTypes,
        excludedIds: excludedSuperBatchIds,
      });
    },
    [batchTypes, bulkFetchSuperBatches, excludedSuperBatchIds]
  );

  useEffect(() => {
    setSuperBatches(value);
  }, [value]);

  useEffect(() => {
    if (isSuccess) {
      const _superBatches = data?.data?.map((o) => ({
        value: o.id,
        label: o.name,
        ...o,
      }));

      const sb = compact(flatten([value, _superBatches]));

      setSuperBatches(sb);
      // check if onChange is not passed
      onChange && onChange(sb);
      reset();
    }
  }, [data, isSuccess, onChange, reset, value]);

  useEffect(() => {
    if (error) {
      cogoToast.error(DEFAULT_ERROR_MESSAGE);
    }
  }, [error]);

  const handleOnChange = useCallback(
    (option) => {
      setSuperBatches(option);
      onChange && onChange(option);
    },
    [onChange]
  );

  const generateOptions = useCallback(
    (superbatches) =>
      superbatches.map((o) => ({
        value: o.id,
        label: o.name,
        ...o,
      })),
    []
  );

  const loadOptions = useDebouncedPromise(
    async (keyword, callback) => {
      if (keyword.length < MIN_KEYWORD_LENGTH) return callback([]);

      const response = await fetchSuperBatches(
        {
          keyword,
          batchTypes,
          excludedIds: excludedSuperBatchIds,
          superBatchGroupId,
        },
        true
      );
      let options = [];
      if (response.data) {
        options = generateOptions(response.data.data);
      } else {
        cogoToast.error(
          response.error?.data?.message ||
            'Failed to load super batches! Try again after sometime'
        );
      }
      callback(options);
    },
    DEBOUNCE_DURATION,
    [excludedSuperBatchIds, generateOptions]
  );

  const noOptionsMessage = useCallback(
    ({ inputValue }) => {
      if (inputValue.length < MIN_KEYWORD_LENGTH) {
        return `Type atleast ${MIN_KEYWORD_LENGTH} characters to search`;
      } else if (isFetching) {
        return `Searching...`;
      } else {
        return `No matching super batches found.`;
      }
    },
    [isFetching]
  );

  return (
    <div className={classNames(styles.container, containerClassName)}>
      <GlobalLoader isVisible={isBulkFetching} />
      <Select
        type="async"
        placeholder="Select Batch/es"
        components={{
          Option: SuperBatchOption,
          ...(isMulti && { Input: SuperBatchInput }),
        }}
        loadOptions={loadOptions}
        isDisabled={isBulkFetching || isDisabled}
        noOptionsMessage={noOptionsMessage}
        isMulti={isMulti}
        value={superBatches}
        handleOnPaste={handleOnPaste}
        onChange={handleOnChange}
        {...remainingProps}
      />
      <FieldErrors
        errors={error?.data?.message}
        className="m-t-xs"
      />
    </div>
  );
}

SuperBatchSelect.propTypes = {
  excludedSuperBatchIds: PropTypes.arrayOf(PropTypes.number),
};
